// @ts-nocheck
import {
  GeoCoordinates,
  PairedSalesAnalysisFeature,
  ToolkitProperty,
  ToolkitRecordID,
  expected,
  isNull,
  isUndefined,
  parseToolkitListingID,
} from '@aloft/toolkit-shared';
import { Block, Typography, makePxlStr } from '@aloft/toolkit-sui';
import { bbox, bboxPolygon, lineString } from '@turf/turf';

import { useCallback, useEffect, useReducer, useRef } from 'react';

import { CustomToolkitPropertyTable } from '../../../../components/ToolkitPropertyTable/CustomToolkitPropertyTable.tsx';
import { scrollToIndex } from '../../../../components/ToolkitPropertyTable/utils/scrollToIndex.ts';
import MapBox from '../../../../components/map/MapBox.ts';
import { Coordinates } from '../../../../components/map/types.ts';
import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import {
  setActiveID as setActiveReferenceBarID,
  setProperties as setReferenceBarProperties,
  setReferenceViewOpen,
} from '../../../../redux/features/referenceBarUISlice.ts';
import { selectReferenceBarUI } from '../../../../redux/features/selectors/referenceBarUISelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { formatReferenceViewID } from '../../../referenceView/index.ts';
import { PairedSaleIDs, PairedSalesFormSchema } from '../../types.ts';
import { PairedSalesMap } from '../PairedSalesMap/PairedSalesMap.tsx';
import { PairedSalesMapProps } from '../PairedSalesMap/types.ts';
import { MapAlerts } from './MapAlerts.tsx';
import { PickPairHeader } from './PickPairHeader.tsx';
import { HEADER_HEIGHT_IN_PX } from './constants.ts';
import { getSelectedAndActiveRowIndices } from './getSelectedAndActiveRowIndices.ts';
import { getTableColumns } from './getTableColumns.tsx';
import { makePairedSalesTableRows } from './makePairedSalesTableRows.ts';
import { PairedSalesTableRow } from './types.ts';

type Props = {
  term: PairedSalesAnalysisFeature;
  mapProps: Pick<PairedSalesMapProps, 'subject' | 'center' | 'zoom'>;
  isLoading: boolean;
  pairs: PairedSaleIDs[];
  recordIDToPropertyMap: Record<ToolkitRecordID, ToolkitProperty>;
  properties: ToolkitProperty[];
  coordinates: GeoCoordinates;
  onSelectPair: (p1: ToolkitProperty, p2: ToolkitProperty) => void;
  onDeselectPair: (p1: ToolkitProperty, p2: ToolkitProperty) => void;
  onPolygonCreateAndUpdate: (coordinates: GeoCoordinates) => Promise<void>;
  onPolygonDrawDelete: () => void;
  onClickEditFilters: () => void;
  onMoveEnd: (center: Coordinates, zoom: number) => void;
};

export const PickPair = ({
  term,
  isLoading,
  onSelectPair,
  onDeselectPair,
  pairs,
  recordIDToPropertyMap,
  properties,
  mapProps,
  coordinates,
  onClickEditFilters,
  onMoveEnd,
  onPolygonCreateAndUpdate,
  onPolygonDrawDelete,
}: Props) => {
  const mapRef = useRef<MapBox | null>(null);
  const [activePair, setActivePair] = useReducer(
    (prev: PairedSaleIDs | null, next: PairedSaleIDs | null) =>
      prev?.p1 === next?.p1 && prev?.p2 === next?.p2 ? null : next,
    null,
  );

  const { selectedDataSheet: activeReferenceBarDataSheet } = useAppSelector(selectReferenceBarUI);
  const dispatch = useAppDispatch();

  const { watch } = useZodFormContext<PairedSalesFormSchema>();

  const selectedPairs = watch('selectedPairs').map((pair) => ({
    p1: expected(pair.p1.toolkitRecordID),
    p2: expected(pair.p2.toolkitRecordID),
  }));

  function handlePolygonDrawDelete() {
    onPolygonDrawDelete();
    dispatch(setReferenceBarProperties([]));
  }

  const { activeRowIndex, selectedRowIndices } = getSelectedAndActiveRowIndices({
    pairs,
    activePair,
    selectedPairs,
  });

  const centerPairOnMap = (pair: PairedSaleIDs) => {
    if (!mapRef.current) return;

    const p1 = recordIDToPropertyMap[pair.p1];
    const p2 = recordIDToPropertyMap[pair.p2];

    if (isUndefined(p1) || isUndefined(p2)) return;
    if ([p1.longitude, p1.latitude, p2.longitude, p2.latitude].some(isUndefined)) return;

    const line = lineString([
      [p1.longitude!, p1.latitude!],
      [p2.longitude!, p2.latitude!],
    ]);

    const bounds = bboxPolygon(bbox(line)).bbox;
    const map = mapRef.current.getMap();

    // @ts-expect-error @zachsnoek to fix this type error
    map.fitBounds(bounds, { padding: 50 });
  };

  // WARNING: DO NOT DO THIS! This is hack to access the reference bar's
  //  activeID in the effect below without running the effect when it changes.
  //  Ideally that effect would not exist at all, but that's a big change to
  //  get this small piece of a feature working. In the future, this could be
  //  done with useEffectEvent
  //  (https://react.dev/learn/removing-effect-dependencies#do-you-want-to-read-a-value-without-reacting-to-its-changes)
  const activeReferenceBarDataSheetRef = useRef(activeReferenceBarDataSheet);
  if (activeReferenceBarDataSheetRef.current !== activeReferenceBarDataSheet) {
    activeReferenceBarDataSheetRef.current = activeReferenceBarDataSheet;
  }

  useEffect(() => {
    if (isNull(activePair)) {
      dispatch(setReferenceBarProperties([]));
      // HACK: Don't automatically close the reference bar if the help drawer
      //  is open. This lets the automatic help drawer open behavior work for
      //  paired sales.
      if (activeReferenceBarDataSheetRef.current !== 'help-drawer') {
        dispatch(setReferenceViewOpen(false));
      }
      return;
    }

    const p1 = recordIDToPropertyMap[activePair.p1];
    const p2 = recordIDToPropertyMap[activePair.p2];
    if (!p1?.toolkitListingID || !p2?.toolkitListingID) {
      console.error('Active pair is missing valid `toolkitListingID`(s): ', activePair);
      return;
    }

    const p1ParsedID = parseToolkitListingID(p1.toolkitListingID);
    const p2ParsedID = parseToolkitListingID(p2.toolkitListingID);

    dispatch(setActiveReferenceBarID(formatReferenceViewID(p1ParsedID)));
    dispatch(
      setReferenceBarProperties([
        { id: formatReferenceViewID(p1ParsedID), mapPinLabel: '1' },
        { id: formatReferenceViewID(p2ParsedID), mapPinLabel: '2' },
      ]),
    );
  }, [activePair, dispatch, recordIDToPropertyMap]);

  const handleMapPinClick = (pair: PairedSaleIDs) => {
    const { activeRowIndex } = getSelectedAndActiveRowIndices({
      pairs,
      activePair: pair,
      selectedPairs: [],
    });
    if (activeRowIndex !== -1) {
      scrollToIndex(activeRowIndex);
    }
    setActivePair(pair);
  };

  const handleTableRowClick = (row: PairedSalesTableRow) => {
    if (isUndefined(row.p1.toolkitRecordID) || isUndefined(row.p2.toolkitRecordID)) {
      console.error('Property missing toolkitRecordID');
      return;
    }
    const pair: PairedSaleIDs = {
      p1: row.p1.toolkitRecordID,
      p2: row.p2.toolkitRecordID,
    };

    setActivePair(pair);
    centerPairOnMap(pair);
  };

  const tableRows = makePairedSalesTableRows({ pairs, properties: recordIDToPropertyMap });

  const handleTableRowSelect = (row: PairedSalesTableRow, isSelecting: boolean) => {
    if (isUndefined(row.p1.toolkitRecordID) || isUndefined(row.p2.toolkitRecordID)) {
      console.error('Property missing toolkitRecordID');
      return;
    }
    const pair: PairedSaleIDs = {
      p1: row.p1.toolkitRecordID,
      p2: row.p2.toolkitRecordID,
    };

    if (isSelecting) {
      onSelectPair(row.p1, row.p2);
      setActivePair(pair);
      centerPairOnMap(pair);
    } else {
      onDeselectPair(row.p1, row.p2);
    }
  };

  const handleTooltipClose = useCallback(() => {
    setActivePair(null);
  }, []);

  const hasDrawnPolygon = !!coordinates.length;

  return (
    <Block height="100%" width="100%">
      <Block height={makePxlStr(HEADER_HEIGHT_IN_PX)}>
        <PickPairHeader
          term={term}
          numResults={pairs.length}
          numProperties={properties.length}
          onClickEditFilters={onClickEditFilters}
          isEditFiltersDisabled={!hasDrawnPolygon}
        />
      </Block>
      <Block height={`calc(100% - ${makePxlStr(HEADER_HEIGHT_IN_PX)})`}>
        <Block height="50%" width="100%" position="relative">
          <MapAlerts
            hasDrawnPolygon={hasDrawnPolygon}
            hasResults={pairs.length > 0}
            isLoading={isLoading}
          />
          <PairedSalesMap
            mapRef={mapRef}
            pairs={pairs}
            properties={recordIDToPropertyMap}
            coordinates={coordinates}
            selectedPairs={selectedPairs}
            activePair={activePair}
            {...mapProps}
            onPinClick={handleMapPinClick}
            onTooltipClose={handleTooltipClose}
            onPolygonCreateAndUpdate={onPolygonCreateAndUpdate}
            onPolygonDrawDelete={handlePolygonDrawDelete}
            onMoveEnd={onMoveEnd}
          />
        </Block>
        <Block height="50%" width="100%">
          <CustomToolkitPropertyTable
            key={JSON.stringify(pairs)} // TODO: Better way to refresh table data
            columns={getTableColumns(term)}
            data={tableRows}
            tpColumnConfig={{
              toValue: (get, data) => [get(data.p1), get(data.p2)],
              toCell: ({ value: [p1, p2], toString }) => (
                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <span>
                    <Typography variant="tableH2" truncate>
                      {toString(p1)}
                    </Typography>
                  </span>
                  <span>
                    <Typography variant="tableH2" truncate>
                      {toString(p2)}
                    </Typography>
                  </span>
                </span>
              ),
              toCompareValues: (rowAValues, rowBValues) => {
                const a = rowAValues?.[0];
                const b = rowBValues?.[0];
                return [a, b];
              },
            }}
            selectedRowIndices={selectedRowIndices}
            activeRowIndex={activeRowIndex}
            emptyTableMessage="No pairs found"
            enableMultiSelect
            memoizeRows
            onRowClick={handleTableRowClick}
            onRowSelect={handleTableRowSelect}
          />
        </Block>
      </Block>
    </Block>
  );
};
